import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, Link } from "react-router-dom";
import {
  FiAlertTriangle,
  FiUserX,
  FiRefreshCw,
  FiArrowLeft,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useUserContext } from "../../context/UserContext";
import collaboratorsAPI from "../../services/collaboratorsAPI";
import professionalProfileAPI from "../../services/professionalProfileAPI";
import { profileDashboardService } from "../../services/profileDashboardService";
import feedService from "../../services/feedService";
import "./ProfessionalProfilePage.css";
import ProfileHeader from "./ProfileHeader";

import TabNavigation from "./TabNavigation";
import FollowModals from "./FollowModals";
import EndorsementsTab from "./EndorsementsTab";
import ActivityTab from "./ActivityTab";
import OverviewTab from "./OverviewTab";
import PostsTab from "./PostsTab";
import ResearchTab from "./ResearchTab";
import ReviewsTab from "./ReviewsTab";
import CollaboratorsModal from "./CollaboratorsModal";
import CollabRequestModal from "../collaborators/CollabRequestModal";
import ProfileSkeleton from "./ProfileSkeleton";

const ProfessionalProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useUserContext();

  // Utility function to get field image
  const getFieldImage = (field) => {
    if (!field) return "/field-images/default.jpg";

    const fieldName = field
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    return `/field-images/${fieldName}.jpg`;
  };

  // Utility function to get community image with fallbacks
  const getCommunityImage = (community) => {
    // First priority: Community's actual signed photo URL (backend uses 'signedPhotoURL')
    if (community.signedPhotoURL) {
      return community.signedPhotoURL;
    }

    // Second priority: Community's photo URL (backend uses 'photoURL')
    if (community.photoURL) {
      return community.photoURL;
    }

    // Third priority: Integration guide format (photoUrl) - for compatibility
    if (community.photoUrl) {
      return community.photoUrl;
    }

    // Fourth priority: Field-based image if community has a field
    if (community.field) {
      return getFieldImage(community.field);
    }

    // Final fallback: Default image
    return "/field-images/default.jpg";
  };

  // Refs for scroll position preservation
  const followersListRef = useRef(null);
  const followingListRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Core Profile Data
  const [profile, setProfile] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [collaborationStats, setCollaborationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stats Data
  const [stats, setStats] = useState({
    posts: 0,
    rooms: 0,
    communities: 0,
    workshops: 0,
    followers: 0,
    following: 0,
    rating: 0,
    averageRating: 0, // ✅ NEW: Rating data from profileStats
    totalRatings: 0, // ✅ NEW: Rating data from profileStats
  });

  // Activity Data with real API integration
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [postsPagination, setPostsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPosts: 0,
    hasMore: false,
  });

  const [researchRooms, setResearchRooms] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [skills, setSkills] = useState([]);

  // Post interaction states
  const [postFilter, setPostFilter] = useState("all");
  const [postSort, setPostSort] = useState("recent");
  const [refreshingUrls, setRefreshingUrls] = useState(new Set());

  // Reviews state (shared between OverviewTab and ReviewsTab)
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState("overview");
  const [isFollowing, setIsFollowing] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [showCollaborationRequestModal, setShowCollaborationRequestModal] =
    useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [followingData, setFollowingData] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);

  // Loading states for individual operations
  const [unfollowingUsers, setUnfollowingUsers] = useState(new Set());
  const [followingUsers, setFollowingUsers] = useState(new Set());

  // ✅ SCROLL RESTORATION for FOLLOWING modal
  useLayoutEffect(() => {
    if (
      showFollowingModal &&
      followingListRef.current &&
      scrollPositionRef.current > 0
    ) {
      followingListRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [showFollowingModal, followingData, unfollowingUsers, followingUsers]); // Restore after ANY changes

  // ✅ SCROLL RESTORATION for FOLLOWERS modal
  useLayoutEffect(() => {
    if (
      showFollowersModal &&
      followersListRef.current &&
      scrollPositionRef.current > 0
    ) {
      followersListRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [showFollowersModal, followersData, followingUsers]); // Restore after followers data changes

  const isOwnProfile = useMemo(
    () => !userId || userId === currentUser?.id,
    [userId, currentUser?.id]
  );

  // Memoized profile loading function to prevent unnecessary re-renders
  const loadProfileData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (isOwnProfile) {
        // Load own profile with complete data - SINGLE API CALL
        const profileData = await collaboratorsAPI.profile.getMyProfile();
        setProfile(profileData);

        // Extract profileStats directly from profile response (no extra API call needed!)
        const profileStats = profileData?.profileStats || {};

        // Use backend uniqueCollaboratorsCount, fallback to real-time calculation only if missing
        let collaboratorsCount = profileStats?.uniqueCollaboratorsCount;
        if (!collaboratorsCount && collaboratorsCount !== 0) {
          collaboratorsCount = await calculateCollaboratorsCount(userId);
        }

        // Map stats from profileStats - NO DUPLICATE API CALL
        const mappedStats = {
          posts: profileStats?.postsCount || 0,
          rooms: profileStats?.researchRoomsCount || 0,
          communities: profileStats?.communitiesCount || 0,
          workshops: profileStats?.workshopsCount || 0,
          collaborators: collaboratorsCount || 0,
          followers: profileStats?.followersCount || 0,
          following: profileStats?.followingCount || 0,
          rating: profileData?.averageRating || 0,
          averageRating: profileData?.averageRating || 0,
          totalRatings: profileData?.totalRatings || 0,
        };

        setStats(mappedStats);

        // NOTE: Posts, rooms, communities, workshops are loaded on-demand when tabs are clicked
        // This reduces initial API calls from 10+ to just 1 (profile call)
        // Activity, skills, and reviews are also lazy-loaded
      } else {
        // Load other user's profile - SINGLE API CALL for profile + stats
        const data = await collaboratorsAPI.profile.getUserProfile(userId);
        setProfile(data.profile);
        setRelationship(data.relationship);
        setCollaborationStats(data.collaborationStats);
        setIsFollowing(data.relationship?.isFollowing || false);

        // Extract profileStats directly from profile response (no extra API call needed!)
        const profileStats = data.profile?.profileStats || {};

        // Use backend uniqueCollaboratorsCount, fallback to real-time calculation only if missing
        let collaboratorsCount = profileStats?.uniqueCollaboratorsCount;
        if (!collaboratorsCount && collaboratorsCount !== 0) {
          collaboratorsCount = await calculateCollaboratorsCount(userId);
        }

        // Map stats from profileStats - NO DUPLICATE API CALL
        const mappedStats = {
          posts: profileStats?.postsCount || 0,
          rooms: profileStats?.researchRoomsCount || 0,
          communities: profileStats?.communitiesCount || 0,
          workshops: profileStats?.workshopsCount || 0,
          collaborators: collaboratorsCount || 0,
          followers: profileStats?.followersCount || 0,
          following: profileStats?.followingCount || 0,
          rating: data.profile?.averageRating || 0,
          averageRating: data.profile?.averageRating || 0,
          totalRatings: data.profile?.totalRatings || 0,
        };

        setStats(mappedStats);

        // NOTE: Posts, rooms, communities, workshops are loaded on-demand when tabs are clicked
        // This reduces initial API calls from 10+ to just 1 (profile call)
        // Activity, skills, and reviews are also lazy-loaded
      }
    } catch (error) {
setError(error.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [userId, isOwnProfile]);

  // Load posts with pagination and filters - defined early to avoid hoisting issues
  const loadPosts = useCallback(
    async (page = 1, limit = 10, filter = null) => {
      try {
        setPostsLoading(true);
        setPostsError(null);

        const targetUserId = userId || currentUser?.id;
        const response = await professionalProfileAPI.posts.getUserPosts(
          targetUserId,
          page,
          limit
        );

        const postsData =
          response?.data?.posts || response?.posts || response || [];
        const paginationData =
          response?.data?.pagination || response?.pagination || {};

        if (page === 1) {
          setPosts(postsData);
        } else {
          setPosts((prev) => [...prev, ...postsData]);
        }

        setPostsPagination({
          currentPage: paginationData.currentPage || page,
          totalPages: paginationData.totalPages || 1,
          totalPosts: paginationData.total || postsData.length,
          hasMore: paginationData.hasMore || false,
        });
      } catch (error) {
setPostsError("Failed to load posts");
        toast.error("Failed to load posts");
      } finally {
        setPostsLoading(false);
      }
    },
    [userId, currentUser?.id]
  );

  // Load reviews data - LAZY LOADED on tab click
  const loadReviewsData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId) return;

    try {
      const [ratingsData, statsData] = await Promise.all([
        collaboratorsAPI.rating.getUserRatings(profileUserId, 1, 20),
        collaboratorsAPI.rating.getRatingStats(profileUserId),
      ]);

      setReviews(ratingsData?.ratings || []);
      setReviewStats(statsData);
    } catch (error) { /* ignored */ }
  }, [userId, currentUser?.id]);

  // Load rooms data - LAZY LOADED on tab click
  const loadRoomsData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId || researchRooms.length > 0) return; // Don't reload if already loaded

    try {
      const roomsData = await professionalProfileAPI.rooms.getUserRooms(
        profileUserId
      );
      const extractedRooms =
        roomsData?.data?.rooms || roomsData?.rooms || roomsData || [];
      setResearchRooms(extractedRooms);
    } catch (error) { /* ignored */ }
  }, [userId, currentUser?.id, researchRooms.length]);

  // Load communities data - LAZY LOADED on tab click
  const loadCommunitiesData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId || communities.length > 0) return; // Don't reload if already loaded

    try {
      const communitiesData =
        await professionalProfileAPI.communities.getUserCommunities(
          profileUserId
        );
      // API now returns processed array directly
      const extractedCommunities = Array.isArray(communitiesData) 
        ? communitiesData 
        : (communitiesData?.data?.communities ||
           communitiesData?.communities ||
           communitiesData ||
           []);
      setCommunities(extractedCommunities);
} catch (error) { /* ignored */ }
  }, [userId, currentUser?.id, communities.length]);

  // Load workshops data - LAZY LOADED on tab click
  const loadWorkshopsData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId || workshops.length > 0) return; // Don't reload if already loaded

    try {
      const workshopsData =
        await professionalProfileAPI.workshops.getUserWorkshops(profileUserId);
      // API now returns processed array directly
      const extractedWorkshops = Array.isArray(workshopsData)
        ? workshopsData
        : (workshopsData?.data?.workshops ||
           workshopsData?.workshops ||
           workshopsData ||
           []);
      setWorkshops(extractedWorkshops);
} catch (error) { /* ignored */ }
  }, [userId, currentUser?.id, workshops.length]);

  // Load activity data - LAZY LOADED on tab click
  const loadActivityData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId || activityFeed.length > 0) return; // Don't reload if already loaded

    try {
      const activityData =
        await professionalProfileAPI.activity.getUserActivity(profileUserId);
      const extractedActivities =
        activityData?.activities ||
        activityData?.data?.activities ||
        activityData ||
        [];
      setActivityFeed(extractedActivities);
    } catch (error) { /* ignored */ }
  }, [userId, currentUser?.id, activityFeed.length]);

  // Load skills data - LAZY LOADED on tab click
  const loadSkillsData = useCallback(async () => {
    const profileUserId = userId || currentUser?.id;
    if (!profileUserId || skills.length > 0) return; // Don't reload if already loaded

    try {
      const skillsData = await collaboratorsAPI.endorsement.getUserSkills(
        profileUserId
      );
      setSkills(skillsData || []);
    } catch (error) {
setSkills([]);
    }
  }, [userId, currentUser?.id, skills.length]);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Load all Overview tab data on initial page load
  // Overview tab displays: posts, research rooms, activity, skills, and reviews
  useEffect(() => {
    if (profile && (userId || currentUser?.id)) {
      // Load posts (shown in "Latest Posts" section)
      if (posts.length === 0) {
        loadPosts(1, 10);
      }
      
      // Load research rooms (shown in "Active Research Spaces" section)
      if (researchRooms.length === 0) {
        loadRoomsData();
      }
      
      // Load communities (shown in Overview communities row AND Research tab)
      if (communities.length === 0) {
        loadCommunitiesData();
      }
      
      // Load workshops (shown in Overview AND Research tab)
      if (workshops.length === 0) {
        loadWorkshopsData();
      }
      
      // Load activity feed (shown in "Recent Activity" section)
      if (activityFeed.length === 0) {
        loadActivityData();
      }
      
      // Load skills (shown in "Top Skills" section)
      if (skills.length === 0) {
        loadSkillsData();
      }
      
      // Load reviews (shown in "Reviews & Ratings" section)
      if (reviews.length === 0) {
        loadReviewsData();
      }
    }
  }, [
    profile,
    userId,
    currentUser?.id,
    loadPosts,
    loadRoomsData,
    loadCommunitiesData,
    loadWorkshopsData,
    loadActivityData,
    loadSkillsData,
    loadReviewsData,
    posts.length,
    researchRooms.length,
    communities.length,
    workshops.length,
    activityFeed.length,
    skills.length,
    reviews.length,
  ]);

  // Handle tab changes - lazy load data for each tab
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);

      // Lazy load data based on tab
      switch (tab) {
        case "reviews":
          if (reviews.length === 0) loadReviewsData();
          break;
        case "research":
          loadRoomsData();
          loadCommunitiesData();
          loadWorkshopsData();
          break;
        case "activity":
          loadActivityData();
          break;
        case "endorsements":
          loadSkillsData();
          break;
        default:
          break;
      }
    },
    [
      loadReviewsData,
      loadRoomsData,
      loadCommunitiesData,
      loadWorkshopsData,
      loadActivityData,
      loadSkillsData,
      reviews.length,
    ]
  );

  const handleFollow = useCallback(
    async (userIdToFollow) => {
      const targetUserId = userIdToFollow || userId;

      try {
        if (isFollowing) {
          await professionalProfileAPI.social.unfollowUser(targetUserId);
          setIsFollowing(false);
          setStats((prev) => ({ ...prev, followers: prev.followers - 1 }));
          toast.success("Unfollowed successfully");
        } else {
          await professionalProfileAPI.social.followUser(targetUserId);
          setIsFollowing(true);
          setStats((prev) => ({ ...prev, followers: prev.followers + 1 }));
          toast.success("Following successfully");
        }
        // Invalidate profile cache after follow/unfollow action
        profileDashboardService.invalidateCache(targetUserId);
        profileDashboardService.invalidateBackendCache(targetUserId);
      } catch (error) {
toast.error("Failed to update follow status");
      }
    },
    [isFollowing, userId]
  );

  const loadFollowingData = useCallback(async () => {
    try {
      setFollowingLoading(true);
      const data = await professionalProfileAPI.social.getFollowing(
        userId || currentUser?.id
      );
      // Handle different response structures
      let extractedFollowing = [];
      if (Array.isArray(data)) {
        extractedFollowing = data;
      } else if (data?.following) {
        extractedFollowing = data.following;
      } else if (data?.data?.following) {
        extractedFollowing = data.data.following;
      }

      // Filter out null/undefined entries and initialize isFollowing state
      const followingWithState = extractedFollowing
        .filter((user) => user != null)
        .map((user) => ({
          ...user,
          isFollowing: user.isFollowing !== false, // Default to true unless explicitly false
        }));

      setFollowingData(followingWithState);

      // Update the following count in stats
      setStats((prevStats) => ({
        ...prevStats,
        following: followingWithState.length,
      }));
    } catch (error) {
toast.error("Failed to load following");
      setFollowingData([]); // Set empty array on error
    } finally {
      setFollowingLoading(false);
    }
  }, [userId, currentUser?.id]);

  // Instagram-style unfollow: Keep user in list, change button to "Follow"
  const handleUnfollow = useCallback(
    async (userIdToUnfollow) => {
      if (unfollowingUsers.has(userIdToUnfollow)) return; // Prevent double clicks

      // Store scroll position BEFORE any state updates
      const scrollTop = followingListRef.current?.scrollTop || 0;
      scrollPositionRef.current = scrollTop;

      try {
        // ✅ SINGLE BATCHED STATE UPDATE - React 18 automatic batching
        setUnfollowingUsers((prev) => new Set([...prev, userIdToUnfollow]));
        setFollowingData((prev) =>
          prev.map((user) =>
            user.id === userIdToUnfollow
              ? { ...user, isFollowing: false }
              : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following - 1 }));

        // Make API call
        await professionalProfileAPI.social.unfollowUser(userIdToUnfollow);

        // Invalidate cache after unfollow
        profileDashboardService.invalidateCache();
        profileDashboardService.invalidateBackendCache();

        // ✅ IMMEDIATE TOAST - no delays
        toast.success("Unfollowed successfully");
      } catch (error) {
toast.error("Failed to unfollow user");

        // ✅ ON ERROR - revert optimistic update (scroll handled by useLayoutEffect)
        setFollowingData((prev) =>
          prev.map((user) =>
            user.id === userIdToUnfollow ? { ...user, isFollowing: true } : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following + 1 }));
      } finally {
        // ✅ CLEAN REMOVAL - no additional setTimeout
        setUnfollowingUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userIdToUnfollow);
          return newSet;
        });
      }
    },
    [unfollowingUsers]
  );

  // ==================== POST INTERACTION FUNCTIONS ====================

  // Handle post like/unlike
  const handlePostLike = useCallback(
    async (postId) => {
      try {
        const result = await feedService.toggleLike(postId);

        setPosts((prev) =>
          prev.map((post) =>
            post._id === postId
              ? {
                  ...post,
                  likeCount:
                    result.data?.likeCount ||
                    result.likeCount ||
                    (result.liked ? post.likeCount + 1 : post.likeCount - 1),
                  likes: result.liked
                    ? [...(post.likes || []), currentUser?.id]
                    : (post.likes || []).filter((id) => id !== currentUser?.id),
                }
              : post
          )
        );

        toast.success(result.liked ? "Post liked!" : "Post unliked!");
      } catch (error) {
toast.error("Failed to update like status");
      }
    },
    [currentUser?.id]
  );

  // Handle adding comment
  const handleAddComment = useCallback(async (postId, content) => {
    try {
      const newComment = await feedService.addComment(postId, { content });

      setPosts((prev) =>
        prev.map((post) =>
          post._id === postId
            ? {
                ...post,
                comments: [
                  ...(post.comments || []),
                  newComment.data || newComment,
                ],
                commentCount: (post.commentCount || 0) + 1,
              }
            : post
        )
      );

      toast.success("Comment added!");
      return newComment;
    } catch (error) {
toast.error("Failed to add comment");
      throw error;
    }
  }, []);

  // Handle comment like/unlike
  const handleCommentLike = useCallback(
    async (postId, commentId) => {
      try {
        const result = await feedService.toggleCommentLike(postId, commentId);

        setPosts((prev) =>
          prev.map((post) =>
            post._id === postId
              ? {
                  ...post,
                  comments: (post.comments || []).map((comment) =>
                    comment._id === commentId
                      ? {
                          ...comment,
                          likeCount:
                            result.data?.likeCount ||
                            result.likeCount ||
                            (result.liked
                              ? comment.likeCount + 1
                              : comment.likeCount - 1),
                          likes: result.liked
                            ? [...(comment.likes || []), currentUser?.id]
                            : (comment.likes || []).filter(
                                (id) => id !== currentUser?.id
                              ),
                        }
                      : comment
                  ),
                }
              : post
          )
        );
      } catch (error) {
toast.error("Failed to update comment like");
      }
    },
    [currentUser?.id]
  );

  // Handle adding reply to comment
  const handleAddReply = useCallback(async (postId, commentId, content) => {
    try {
      const newReply = await feedService.addCommentReply(
        postId,
        commentId,
        content
      );

      setPosts((prev) =>
        prev.map((post) =>
          post._id === postId
            ? {
                ...post,
                comments: (post.comments || []).map((comment) =>
                  comment._id === commentId
                    ? {
                        ...comment,
                        replies: [
                          ...(comment.replies || []),
                          newReply.data || newReply,
                        ],
                      }
                    : comment
                ),
              }
            : post
        )
      );

      toast.success("Reply added!");
      return newReply;
    } catch (error) {
toast.error("Failed to add reply");
      throw error;
    }
  }, []);

  // Handle repost
  const handleRepost = useCallback(async (postId, caption = "") => {
    try {
      const newRepost = await feedService.repostPost(postId, caption);

      // Update the original post's repost count
      setPosts((prev) =>
        prev.map((post) =>
          post._id === postId
            ? { ...post, repostCount: (post.repostCount || 0) + 1 }
            : post
        )
      );

      toast.success("Post reposted!");
      return newRepost;
    } catch (error) {
toast.error("Failed to repost");
      throw error;
    }
  }, []);

  // Handle expired media URLs
  const handleMediaError = useCallback(
    async (postId) => {
      if (refreshingUrls.has(postId)) return;

      try {
        setRefreshingUrls((prev) => new Set([...prev, postId]));

        const freshPost = await feedService.getPostById(postId);

        setPosts((prev) =>
          prev.map((post) =>
            post._id === postId
              ? { ...post, ...(freshPost.data || freshPost) }
              : post
          )
        );
      } catch (error) { /* ignored */ } finally {
        setRefreshingUrls((prev) => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
      }
    },
    [refreshingUrls]
  );

  const handleFollowFromModal = useCallback(
    async (userIdToFollow) => {
      if (followingUsers.has(userIdToFollow)) return; // Prevent double clicks

      // ✅ STORE SCROLL POSITION for followers modal
      const scrollTop = followersListRef.current?.scrollTop || 0;
      scrollPositionRef.current = scrollTop;

      try {
        // ✅ SINGLE BATCHED STATE UPDATE - for followers modal
        setFollowingUsers((prev) => new Set([...prev, userIdToFollow]));
        setFollowersData((prev) =>
          prev.map((user) =>
            user.id === userIdToFollow ? { ...user, isFollowing: true } : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following + 1 }));

        await professionalProfileAPI.social.followUser(userIdToFollow);

        // Invalidate cache after follow action
        profileDashboardService.invalidateCache();
        profileDashboardService.invalidateBackendCache();

        toast.success("Following successfully");
      } catch (error) {
toast.error("Failed to follow user");

        // ✅ ON ERROR - revert optimistic update
        setFollowersData((prev) =>
          prev.map((user) =>
            user.id === userIdToFollow ? { ...user, isFollowing: false } : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following - 1 }));
      } finally {
        // ✅ CLEAN REMOVAL
        setFollowingUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userIdToFollow);
          return newSet;
        });
      }
    },
    [followingUsers]
  );

  // Handle follow back from following modal (when user shows "Follow" button)
  const handleFollowBackFromFollowing = useCallback(
    async (userIdToFollow) => {
      if (followingUsers.has(userIdToFollow)) return; // Prevent double clicks

      // ✅ STORE SCROLL POSITION - same as unfollow
      const scrollTop = followingListRef.current?.scrollTop || 0;
      scrollPositionRef.current = scrollTop;

      try {
        // ✅ SINGLE BATCHED STATE UPDATE - same pattern as unfollow
        setFollowingUsers((prev) => new Set([...prev, userIdToFollow]));
        setFollowingData((prev) =>
          prev.map((user) =>
            user.id === userIdToFollow ? { ...user, isFollowing: true } : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following + 1 }));

        await professionalProfileAPI.social.followUser(userIdToFollow);

        // Invalidate cache after follow action
        profileDashboardService.invalidateCache();
        profileDashboardService.invalidateBackendCache();

        toast.success("Following successfully");
      } catch (error) {
toast.error("Failed to follow user");

        // ✅ ON ERROR - revert optimistic update (scroll handled by useLayoutEffect)
        setFollowingData((prev) =>
          prev.map((user) =>
            user.id === userIdToFollow ? { ...user, isFollowing: false } : user
          )
        );
        setStats((prev) => ({ ...prev, following: prev.following - 1 }));
      } finally {
        // ✅ CLEAN REMOVAL - no additional operations
        setFollowingUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userIdToFollow);
          return newSet;
        });
      }
    },
    [followingUsers]
  );

  // Memoized modal handlers to prevent re-renders
  const closeFollowersModal = useCallback(() => {
    setShowFollowersModal(false);
    setFollowersData([]);
  }, []);

  const closeFollowingModal = useCallback(() => {
    setShowFollowingModal(false);
    setFollowingData([]);
  }, []);

  const closeCollaboratorsModal = useCallback(() => {
    setShowCollaboratorsModal(false);
  }, []);

  const handleCollaborate = useCallback(() => {
    if (!isOwnProfile && profile?.isOpenForCollaboration) {
      setShowCollaborationRequestModal(true);
    }
  }, [isOwnProfile, profile?.isOpenForCollaboration]);

  const loadFollowersData = useCallback(async () => {
    try {
      setFollowersLoading(true);
      const data = await professionalProfileAPI.social.getFollowers(
        userId || currentUser?.id
      );
      // Handle different response structures
      let extractedFollowers = [];
      if (Array.isArray(data)) {
        extractedFollowers = data;
      } else if (data?.followers) {
        extractedFollowers = data.followers;
      } else if (data?.data?.followers) {
        extractedFollowers = data.data.followers;
      }

      // Filter out null/undefined entries and initialize isFollowing state
      const followersWithState = extractedFollowers
        .filter((user) => user != null)
        .map((user) => ({
          ...user,
          isFollowing: user.isFollowing || false, // Default to false unless explicitly true
        }));

      setFollowersData(followersWithState);

      // Update the followers count in stats
      setStats((prevStats) => ({
        ...prevStats,
        followers: followersWithState.length,
      }));
    } catch (error) {
toast.error("Failed to load followers");
      setFollowersData([]); // Set empty array on error
    } finally {
      setFollowersLoading(false);
    }
  }, [userId, currentUser?.id]);

  // Handle removing a follower - uses DELETE /api/collaborators/followers/:userId
  const handleRemoveFollower = useCallback(
    async (userIdToRemove) => {
      try {
        // Store scroll position
        const scrollTop = followersListRef.current?.scrollTop || 0;
        scrollPositionRef.current = scrollTop;

        // Optimistically remove from followers data
        setFollowersData((prev) =>
          prev.filter((follower) => follower.id !== userIdToRemove)
        );
        setStats((prev) => ({ ...prev, followers: prev.followers - 1 }));

        // Make API call - uses the removeFollower endpoint
        await professionalProfileAPI.social.removeFollower(userIdToRemove);

        // Invalidate cache after removing follower
        profileDashboardService.invalidateCache();
        profileDashboardService.invalidateBackendCache();

        toast.success("Follower removed successfully");
      } catch (error) {
// Handle specific error cases
        if (error.message && error.message.includes("rate limit")) {
          toast.error("Too many actions. Please wait a moment and try again.");
        } else if (error.message && error.message.includes("not a follower")) {
          toast.error("This user is not following you.");
        } else {
          toast.error("Failed to remove follower. Please try again.");
        }

        // Revert optimistic update on error
        loadFollowersData(); // Reload the data to revert
      }
    },
    [loadFollowersData]
  );

  // Memoized handlers for opening modals
  const openFollowersModal = useCallback(() => {
    setShowFollowersModal(true);

    // ✅ Load data only on first open to preserve optimistic updates
    if (followersData.length === 0) {
      loadFollowersData();
      scrollPositionRef.current = 0; // Reset scroll position when opening
    }
  }, [loadFollowersData, followersData.length]);

  const openFollowingModal = useCallback(() => {
    setShowFollowingModal(true);

    // ✅ Load data only on first open to preserve optimistic updates
    if (followingData.length === 0) {
      loadFollowingData();
      scrollPositionRef.current = 0; // Reset scroll position when opening
    }
  }, [loadFollowingData, followingData.length]);

  const openCollaboratorsModal = useCallback(() => {
    setShowCollaboratorsModal(true);
  }, []);

  // Function to get collaborators count using optimized endpoint
  const calculateCollaboratorsCount = async (targetUserId) => {
    try {
// Use the new optimized collaborators list endpoint with limit=1 to just get the count
      const data = await collaboratorsAPI.collaboration.getCollaboratorsList(
        targetUserId,
        1, // page
        1, // limit - we only need the count, not the data
        "recent" // sortBy
      );

      const collaboratorsCount = data.pagination?.totalCount || 0;
return collaboratorsCount;
    } catch (error) {
return 0; // Return 0 on error
    }
  };

  // Function to refresh the collaborators count
  const refreshCollaboratorsCount = async () => {
    try {
      const newCount = await calculateCollaboratorsCount(userId);

      setStats((prevStats) => ({
        ...prevStats,
        collaborators: newCount,
      }));
    } catch (error) { /* ignored */ }
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <div className="profile-error">
        <FiAlertTriangle className="prf-error-icon" />
        <h3>Error Loading Profile</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={loadProfileData}>
          <FiRefreshCw />
          Try Again
        </button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-error">
        <FiUserX className="prf-error-icon" />
        <h3>Profile Not Found</h3>
        <p>The requested profile could not be found.</p>
        <Link to="/collaborators" className="retry-button">
          <FiArrowLeft />
          Back to Collaborators
        </Link>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <ProfileHeader
        profile={profile}
        isOwnProfile={isOwnProfile}
        isFollowing={isFollowing}
        handleFollow={handleFollow}
        handleCollaborate={handleCollaborate}
        stats={stats}
        openFollowersModal={openFollowersModal}
        openFollowingModal={openFollowingModal}
        openCollaboratorsModal={openCollaboratorsModal}
      />
      <TabNavigation
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        stats={stats}
      />
      <div className="profile-content">
        {activeTab === "overview" && (
          <OverviewTab
            profile={profile}
            posts={posts}
            researchRooms={researchRooms}
            communities={communities}
            workshops={workshops}
            activityFeed={activityFeed}
            stats={stats}
            isOwnProfile={isOwnProfile}
            handleTabChange={handleTabChange}
            getFieldImage={getFieldImage}
            getCommunityImage={getCommunityImage}
            skills={skills}
            reviews={reviews}
            reviewStats={reviewStats}
          />
        )}
        {activeTab === "posts" && (
          <PostsTab
            posts={posts}
            postsLoading={postsLoading}
            postsError={postsError}
            postsPagination={postsPagination}
            postFilter={postFilter}
            setPostFilter={setPostFilter}
            postSort={postSort}
            setPostSort={setPostSort}
            loadPosts={loadPosts}
            handlePostLike={handlePostLike}
            handleAddComment={handleAddComment}
            handleCommentLike={handleCommentLike}
            handleAddReply={handleAddReply}
            handleRepost={handleRepost}
            handleMediaError={handleMediaError}
            currentUser={currentUser}
            isOwnProfile={isOwnProfile}
          />
        )}
        {activeTab === "research" && (
          <ResearchTab
            researchRooms={researchRooms}
            communities={communities}
            workshops={workshops}
            stats={stats}
            isOwnProfile={isOwnProfile}
            getFieldImage={getFieldImage}
            getCommunityImage={getCommunityImage}
          />
        )}
        {activeTab === "activity" && (
          <ActivityTab
            activityFeed={activityFeed}
            isOwnProfile={isOwnProfile}
          />
        )}
        {activeTab === "endorsements" && (
          <EndorsementsTab
            skills={skills}
            isOwnProfile={isOwnProfile}
            userId={userId || currentUser?.id}
            profile={profile}
          />
        )}
        {activeTab === "reviews" && (
          <ReviewsTab
            reviews={reviews}
            reviewStats={reviewStats}
            isOwnProfile={isOwnProfile}
            userId={userId || currentUser?.id}
            profile={profile}
            relationship={relationship}
            collaborationStats={collaborationStats}
            onReviewsUpdate={(newReviews, newStats) => {
              setReviews(newReviews);
              setReviewStats(newStats);
            }}
          />
        )}
      </div>

      {/* Modals */}
      <FollowModals
        showFollowersModal={showFollowersModal}
        showFollowingModal={showFollowingModal}
        closeFollowersModal={closeFollowersModal}
        closeFollowingModal={closeFollowingModal}
        stats={stats}
        followersData={followersData}
        followingData={followingData}
        followersListRef={followersListRef}
        followingListRef={followingListRef}
        scrollPositionRef={scrollPositionRef}
        isOwnProfile={isOwnProfile}
        currentUser={currentUser}
        handleFollowFromModal={handleFollowFromModal}
        handleUnfollow={handleUnfollow}
        handleFollowBackFromFollowing={handleFollowBackFromFollowing}
        handleRemoveFollower={handleRemoveFollower}
        unfollowingUsers={unfollowingUsers}
        followingUsers={followingUsers}
        followersLoading={followersLoading}
        followingLoading={followingLoading}
        loadFollowersData={loadFollowersData}
        loadFollowingData={loadFollowingData}
      />

      <CollaboratorsModal
        showModal={showCollaboratorsModal}
        closeModal={closeCollaboratorsModal}
        isOwnProfile={isOwnProfile}
        currentUser={currentUser}
        profileUserId={userId}
        onCollaboratorsChange={refreshCollaboratorsCount}
      />

      <CollabRequestModal
        isOpen={showCollaborationRequestModal}
        onClose={() => setShowCollaborationRequestModal(false)}
        targetUser={{
          id: userId,
          name: profile?.name || "User",
        }}
        onRequestSent={() => {
          // Optional: Handle request sent callback
}}
      />
    </div>
  );
};

export default ProfessionalProfilePage;
